// Download / Print for a SIGNED note (owner request 2026-10-02).
//
// Every signed note already has a PDF on the BlinkScribe host:
// GET /v1/documents/pdf/{audio_link} returns it as application/pdf (bearer
// auth; the route takes the recording's audiolink with its extension and
// resolves the matching .pdf, including combine/parent fallbacks). The care
// client never renders the PDF itself: "Print" hands the blob to a new tab
// where the browser's PDF viewer offers Print, "Download" saves it under a
// neutral filename (date + recording id, never the patient's name).
//
// Pure helpers, no React. Errors are mapped to a toast message by
// pdfErrorMessage(); callers must never let a rejection reach render.
import blinkscribeapi from '../../api/blinkscribeapi';

export const PDF_NOT_READY_MESSAGE =
  "The signed note's PDF isn't available yet. Try again in a minute.";
export const PDF_FETCH_FAILED_MESSAGE = "Couldn't fetch the note PDF.";
export const PDF_POPUP_BLOCKED_MESSAGE =
  'Your browser blocked the new tab. Allow pop-ups for this site, or use Download.';

// Path of the signed note's PDF for a recording's audiolink (with extension).
export const pdfPath = (audiolink) => `/v1/documents/pdf/${encodeURIComponent(String(audiolink))}`;

const isBlob = (v) => typeof Blob !== 'undefined' && v instanceof Blob;

// Resolves to the PDF Blob. blinkscribeapi's response interceptor already
// unwraps response.data, so the blob usually arrives bare; accept the raw
// axios shape too in case the interceptor chain changes.
export const fetchSignedNotePdf = async (audiolink) => {
  if (!audiolink) {
    const err = new Error('No document on file for this note');
    err.noteDocument = true;
    throw err;
  }
  const res = await blinkscribeapi.get(pdfPath(audiolink), { responseType: 'blob' });
  const blob = isBlob(res) ? res : res?.data;
  if (!isBlob(blob)) {
    const err = new Error('Unexpected PDF response');
    err.noteDocument = true;
    throw err;
  }
  return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
};

// Toast text for a failed fetch. 404 = the PDF is not written yet (right after
// signing) or lives on the other BlinkScribe host; anything else is generic.
export const pdfErrorMessage = (err) => {
  const status = err?.response?.status ?? err?.status;
  return Number(status) === 404 ? PDF_NOT_READY_MESSAGE : PDF_FETCH_FAILED_MESSAGE;
};

const safeRevoke = (url) => {
  try {
    URL.revokeObjectURL(url);
  } catch (_) {
    /* already revoked / unsupported */
  }
};

// Opens the tab that will show the PDF, synchronously, INSIDE the click
// handler's own call stack — before any await. Safari (iPad Safari in the
// clinic) blocks a window.open that follows an await, and Chrome does once its
// ~5 s of transient activation has lapsed (a slow PDF fetch). The tab gets a
// placeholder so it is not a bare blank page while the PDF downloads; the
// caller hands it to openForPrint(blob, { win }) or closePrintWindow(win).
// Returns null when the browser blocked the tab.
export const openPrintWindow = () => {
  let win = null;
  try {
    win = window.open('', '_blank');
  } catch (_) {
    win = null;
  }
  if (!win) return null;
  try {
    win.document.title = 'Signed note';
    win.document.body.innerHTML =
      '<p style="font-family:Tahoma,Arial,sans-serif;font-size:14px;color:#374151;padding:24px">Loading the signed note&hellip;</p>';
  } catch (_) {
    /* the tab exists; the placeholder is cosmetic */
  }
  return win;
};

// Closes a tab from openPrintWindow() that will not get its PDF (fetch
// failed). Safe on null / already closed.
export const closePrintWindow = (win) => {
  if (!win) return;
  try {
    if (!win.closed) win.close();
  } catch (_) {
    /* ignore */
  }
};

// Shows the PDF in a tab (the browser's viewer supplies Print). With `win`
// (from openPrintWindow) that tab is navigated to the object URL — no second
// window.open, so no pop-up blocker is involved; a `win` the user has already
// closed counts as a cancel. Without `win` it falls back to opening a new tab
// here (blocked when not inside a user gesture). Returns false only when the
// browser blocked the tab, so the caller can say so. The object URL is revoked
// once the tab reports load, or after a fallback delay when the viewer never
// fires it (a navigated tab gets a new Window, so the timer is the usual path);
// the viewer keeps the already-fetched document.
export const openForPrint = (blob, { win: preOpened = null, revokeAfterMs = 60000 } = {}) => {
  const url = URL.createObjectURL(blob);
  let win = null;
  if (preOpened) {
    if (preOpened.closed) {
      safeRevoke(url);
      return true;
    }
    try {
      preOpened.location.href = url;
      win = preOpened;
    } catch (_) {
      win = null;
    }
    if (!win) {
      safeRevoke(url);
      closePrintWindow(preOpened);
      return false;
    }
  } else {
    try {
      win = window.open(url, '_blank');
    } catch (_) {
      win = null;
    }
    if (!win) {
      safeRevoke(url);
      return false;
    }
  }
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    safeRevoke(url);
  };
  const timer = setTimeout(finish, revokeAfterMs);
  try {
    win.addEventListener('load', finish, { once: true });
  } catch (_) {
    /* cross-origin or viewer window without DOM access: the timer revokes */
  }
  try {
    win.focus();
  } catch (_) {
    /* ignore */
  }
  return true;
};

// Saves the PDF through a hidden anchor with the download attribute. The URL is
// revoked on a short timer: revoking synchronously aborts the download in some
// browsers (Firefox) before it has started.
export const downloadPdf = (blob, filename, { revokeAfterMs = 1500 } = {}) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'Note.pdf';
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    document.body.removeChild(a);
    setTimeout(() => safeRevoke(url), revokeAfterMs);
  }
};

// YYYY-MM-DD for a date value. A string that starts with a calendar date is
// read as such (new Date("2026-09-30") is UTC midnight and would print the
// previous day in Chicago); anything else goes through Date in local time.
export const dateStamp = (v) => {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'string') {
    const m = v.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// 'Note_<YYYY-MM-DD>_<recordingid>.pdf' — the visit (appointment) date when
// known, else the recording date; parts that are unknown are left out. No
// patient identifiers in the filename.
export const noteFileName = ({ startdate, ts, id } = {}) => {
  const date = dateStamp(startdate) || dateStamp(ts);
  const rec = id === null || id === undefined || id === '' ? '' : String(id).replace(/[^\w-]+/g, '');
  const parts = ['Note', date, rec].filter(Boolean);
  return `${parts.join('_')}.pdf`;
};
